/**
 * Recommendation service for explicit location recommendations
 *
 * Provides API methods for:
 * - Recommending a location (POST /api/v1/recommendation)
 * - Unrecommending a location (POST /api/v1/unrecommendation)
 * - Fetching user's recommended locations
 *
 * Canonical backend contract:
 *   globe-thrivers-app-backend/docs/frontend/RECOMMENDATION_WRITE_API_FRONTEND_GUIDE.md
 */

import axios from 'axios';
import { isRequestAborted } from '@/lib/requestAbort';
import { debug, error as logError } from '@/utils/logger';
import type { EnrichedRecommendationItem } from '@/api/enrichedRecommendation';

const API_BASE_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL || 'https://app-be.globethrivers.com';

/**
 * Location object returned from recommendations endpoint
 */
export interface RecommendedLocation {
  _id: string;
  name?: string;
  google_place_id?: string;
  rec_count?: number;
  google_places_data?: Record<string, unknown>;
}

/**
 * Pagination metadata for recommendation_locations (when page/limit query params are used)
 */
export interface RecommendationLocationsPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

/**
 * Paginated response from GET /api/v1/user/recommendation_locations/:userId?page=&limit=
 */
export interface RecommendationLocationsPaginatedResponse {
  locations: RecommendedLocation[];
  pagination: RecommendationLocationsPagination;
}

/**
 * Response from POST /api/v1/recommendation.
 * Re-exports EnrichedRecommendationItem for callers that import from this service.
 * Store recommendation._id for subsequent PATCH, DELETE, share, and unrecommend calls.
 */
export type { EnrichedRecommendationItem as RecommendResponse };

/**
 * Response from POST /api/v1/unrecommendation.
 */
export interface UnrecommendResponse {
  deleted: boolean;
}

/**
 * Get authentication headers for API requests
 */
function getAuthHeaders(): Record<string, string> {
  const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

  if (!rawAccessToken) {
    logError('[recommendationService] No access token found in localStorage');
    throw new Error('Authentication token not found. User must be logged in.');
  }

  const accessToken = rawAccessToken.startsWith('Bearer ')
    ? rawAccessToken.substring(7)
    : rawAccessToken;

  const headers: Record<string, string> = {
    'Authorization': accessToken,
    'Content-Type': 'application/json'
  };

  const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  if (rawIdToken) {
    const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
    headers['x-id-token'] = idToken;
  }

  return headers;
}

export const recommendationService = {
  /**
   * Create or update a recommendation (bare heart — no enrichment fields).
   * POST /api/v1/recommendation
   * Returns { recommendation, location }; store recommendation._id for edit/delete/share/unrecommend.
   */
  async recommendLocation(locationId: string): Promise<EnrichedRecommendationItem> {
    const endpoint = `${API_BASE_URL}/api/v1/recommendation`;

    debug('[recommendationService] Recommending location', { locationId });

    try {
      const headers = getAuthHeaders();
      const response = await axios.post<EnrichedRecommendationItem>(
        endpoint,
        { locationId },
        { headers },
      );

      debug('[recommendationService] Recommend success', { locationId, id: response.data.recommendation._id });

      return response.data;
    } catch (err) {
      logError('[recommendationService] Error recommending location', {
        locationId,
        error: err,
      });
      throw err;
    }
  },

  /**
   * Remove a recommendation by location id (legacy path — no share-token cascade).
   * Use unrecommendById when you have the recommendation._id for full cascade.
   * POST /api/v1/unrecommendation with { locationId }
   */
  async unrecommendLocation(locationId: string): Promise<UnrecommendResponse> {
    const endpoint = `${API_BASE_URL}/api/v1/unrecommendation`;

    debug('[recommendationService] Unrecommending location by locationId', { locationId });

    try {
      const headers = getAuthHeaders();
      const response = await axios.post<UnrecommendResponse>(
        endpoint,
        { locationId },
        { headers },
      );

      debug('[recommendationService] Unrecommend success', { locationId });

      return response.data;
    } catch (err) {
      logError('[recommendationService] Error unrecommending location', {
        locationId,
        error: err,
      });
      throw err;
    }
  },

  /**
   * Remove a recommendation by its _id (preferred — runs share-token cascade).
   * POST /api/v1/unrecommendation with { recommendationId }
   */
  async unrecommendById(recommendationId: string): Promise<UnrecommendResponse> {
    const endpoint = `${API_BASE_URL}/api/v1/unrecommendation`;

    debug('[recommendationService] Unrecommending by recommendationId', { recommendationId });

    try {
      const headers = getAuthHeaders();
      const response = await axios.post<UnrecommendResponse>(
        endpoint,
        { recommendationId },
        { headers },
      );

      debug('[recommendationService] Unrecommend by id success', { recommendationId });

      return response.data;
    } catch (err) {
      logError('[recommendationService] Error unrecommending by id', {
        recommendationId,
        error: err,
      });
      throw err;
    }
  },

  /**
   * Get logged-in user's recommended locations (requires auth)
   * GET /api/v1/recommendations/locations
   * 
   * @returns Array of location objects the user has recommended
   */
  async getUserRecommendedLocations(): Promise<RecommendedLocation[]> {
    const endpoint = `${API_BASE_URL}/api/v1/recommendations/locations`;
    
    debug('[recommendationService] Fetching user recommendations');
    
    try {
      const headers = getAuthHeaders();
      const response = await axios.get<RecommendedLocation[]>(
        endpoint,
        { 
          headers,
          params: { photoFormat: "identifiers" }
        }
      );
      
      const locations = Array.isArray(response.data) ? response.data : [];
      
      debug('[recommendationService] Fetch recommendations success', { count: locations.length });
      
      return locations;
    } catch (err) {
      logError('[recommendationService] Error fetching user recommended locations', {
        error: err,
      });
      throw err;
    }
  },

  /**
   * Get another user's recommended locations (authenticated viewer).
   * GET /api/v1/user/recommendation_locations/:userId — requires Bearer auth.
   * For logged-out share-link views use getPublicProfileRecommendationLocationsPaginated in share.ts.
   */
  async getRecommendationsByUserId(userId: string): Promise<RecommendedLocation[]> {
    const endpoint = `${API_BASE_URL}/api/v1/user/recommendation_locations/${userId}`;
    debug('[recommendationService] Fetching recommendations by user ID', { userId });
    try {
      const headers = getAuthHeaders();
      const response = await axios.get<RecommendedLocation[]>(endpoint, {
        headers,
        params: { photoFormat: "identifiers" },
      });
      const locations = Array.isArray(response.data) ? response.data : [];
      debug('[recommendationService] Fetch by user ID success', { userId, count: locations.length });
      return locations;
    } catch (err) {
      logError('[recommendationService] Error fetching recommendations by user ID', { userId, error: err });
      throw err;
    }
  },

  /**
   * Paginated recommendation locations for a user (authenticated viewer).
   * GET /api/v1/user/recommendation_locations/:userId?page=&limit= — requires Bearer auth.
   * For logged-out share-link views use getPublicProfileRecommendationLocationsPaginated in share.ts.
   *
   * Optional location-share merge (both required together): share_token + shared_location_id prepends
   * when valid on location-share URLs. The base list (no merge params) already includes recs ∪ share-derived
   * from the server; merge is for deep-link prepend — client may dedupe by _id across pages.
   *
   * @param userId - The user ID to fetch recommendations for
   * @param page - 1-based page index
   * @param limit - Page size (max 100)
   * @param locationShareMerge - When both fields set, passed as query params for server merge/dedupe
   */
  async getRecommendationsByUserIdPaginated(
    userId: string,
    page: number,
    limit: number,
    locationShareMerge?: { shareToken: string; sharedLocationId: string },
    options?: { signal?: AbortSignal }
  ): Promise<RecommendationLocationsPaginatedResponse> {
    const endpoint = `${API_BASE_URL}/api/v1/user/recommendation_locations/${userId}`;
    const params: Record<string, string | number> = {
      photoFormat: "identifiers",
      page,
      limit: Math.min(100, Math.max(1, limit)),
    };
    if (locationShareMerge?.shareToken && locationShareMerge?.sharedLocationId) {
      params.share_token = locationShareMerge.shareToken;
      params.shared_location_id = locationShareMerge.sharedLocationId;
    }
    debug('[recommendationService] Fetching recommendations by user ID (paginated)', {
      userId,
      page,
      limit,
      locationShareMerge: !!locationShareMerge,
    });
    try {
      const headers = getAuthHeaders();
      const response = await axios.get<RecommendationLocationsPaginatedResponse>(endpoint, {
        headers,
        params,
        signal: options?.signal,
      });
      const data = response.data;
      debug('[recommendationService] Fetch by user ID (paginated) success', {
        userId,
        count: data.locations?.length ?? 0,
        page: data.pagination?.page,
        has_next: data.pagination?.has_next,
      });
      return data;
    } catch (err) {
      if (!isRequestAborted(err)) {
        logError('[recommendationService] Error fetching recommendations by user ID (paginated)', { userId, error: err });
      }
      throw err;
    }
  },

  /**
   * Get any user's recommendation count (public, no auth required)
   * GET /api/v1/user/recommendation_count/:userId
   * 
   * @param userId - The user ID to get count for
   * @returns The count of recommendations
   */
  async getRecommendationCount(userId: string): Promise<number> {
    const endpoint = `${API_BASE_URL}/api/v1/user/recommendation_count/${userId}`;
    
    debug('[recommendationService] Fetching recommendation count', { userId });
    
    try {
      const response = await axios.get<{ count: number }>(endpoint);
      
      const count = typeof response.data?.count === 'number' ? response.data.count : 0;
      
      debug('[recommendationService] Fetch count success', { userId, count });
      
      return count;
    } catch (err) {
      logError('[recommendationService] Error fetching recommendation count', {
        userId,
        error: err,
      });
      throw err;
    }
  },
};

